import React, { useState, useEffect, useCallback } from 'react';
import { hasActivePaidSubscription, canSubmitDelete } from './calendarSubscriberDelete';
import { cancelBadgeLabel } from '../Calendar/calendarCancelText';

/**
 * Master admin: Auction Calendar subscribers.
 *
 * Signup and payment live at calendar.yourfirstparcel.com. This tab is for the
 * accounts that do not come through it: the owner's own, and anyone given a
 * complimentary subscription.
 *
 * THE TEMPORARY PASSWORD IS SHOWN ONCE. Only its hash is stored, so there is no
 * way to read it back afterwards and nothing to leak later. If it is lost, the
 * answer is a new one, which is what Reset password is for.
 *
 * DELETE IS PERMANENT AND CAN COST SOMEBODY A SUBSCRIPTION, so it asks twice:
 * the address typed out in full, and for a paying subscriber a separate
 * acknowledgement that their subscription is being cancelled too. Both are
 * re-checked by the API, which is what actually enforces them.
 */

const STATUS_BADGE = {
  active: 'badge-success',
  trial: 'badge-info',
  past_due: 'badge-warning',
  cancelled: 'badge-secondary'
};

const STATUSES = ['active', 'trial', 'past_due', 'cancelled'];
const PLANS = ['comp', 'monthly', 'annual'];

/**
 * Where a Square id is worth following.
 *
 * ONLY THE CUSTOMER IS A LINK. Square publishes no permalink for an individual
 * subscription page: the dashboard route to one is Orders and payments, then
 * Subscriptions, then clicking it. A guessed URL would 404 from an admin screen
 * the owner reaches for when something is already wrong, which is the worst
 * moment to hand somebody a dead link. The customer profile does have a stable
 * shape, and it shows their subscription anyway, so that is the one that links
 * and the subscription id is rendered as something to copy instead.
 */
const SQUARE_CUSTOMER_URL = 'https://app.squareup.com/dashboard/customers/directory';

/** Enough of an id to recognise, with the whole thing on the element itself. */
function shortId(value) {
  const id = String(value || '');
  return id.length > 12 ? `${id.slice(0, 6)}...${id.slice(-4)}` : id;
}

const idCellStyle = {
  fontFamily: 'monospace',
  fontSize: '0.8rem',
  userSelect: 'all'
};

function CalendarSubscribers({ api, inputStyle, cellLabelStyle, panelStyle, onFlash }) {
  const [subscribers, setSubscribers] = useState([]);
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState('comp');
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The subscriber the confirmation modal is open for, plus what has been typed
  // and ticked in it. Held together so closing the modal cannot leave a stale
  // acknowledgement behind for the next one opened.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [typedEmail, setTypedEmail] = useState('');
  const [cancelPaidAcknowledged, setCancelPaidAcknowledged] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openDelete = (subscriber) => {
    setError('');
    setPendingDelete(subscriber);
    setTypedEmail('');
    setCancelPaidAcknowledged(false);
  };

  const closeDelete = () => {
    setError('');
    setPendingDelete(null);
    setTypedEmail('');
    setCancelPaidAcknowledged(false);
  };

  const load = useCallback(async () => {
    try {
      const data = await api('/subscribers');
      if (data) setSubscribers(data.subscribers);
    } catch (err) {
      setError(err.message);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setError('');
    setBusy(true);
    try {
      const data = await api('/subscribers', {
        method: 'POST',
        body: JSON.stringify({ email, plan })
      });
      if (!data) return;
      setIssued({ email: data.subscriber.email, password: data.temporaryPassword });
      setEmail('');
      await load();
      onFlash('Subscriber created');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async (id, status) => {
    setError('');
    try {
      const data = await api(`/subscribers/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      });
      if (!data) return;
      setSubscribers(prev => prev.map(s => (s.id === id ? data.subscriber : s)));
      onFlash('Subscriber updated');
    } catch (err) {
      setError(err.message);
    }
  };

  const resetPassword = async (id) => {
    setError('');
    try {
      const data = await api(`/subscribers/${id}/reset-password`, { method: 'POST' });
      if (!data) return;
      setIssued({ email: data.subscriber.email, password: data.temporaryPassword });
      onFlash('New temporary password issued');
    } catch (err) {
      setError(err.message);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setError('');
    setDeleting(true);
    try {
      const data = await api(`/subscribers/${pendingDelete.id}`, {
        method: 'DELETE',
        body: JSON.stringify({
          confirmEmail: typedEmail,
          cancelPaidSubscription: cancelPaidAcknowledged
        })
      });
      if (!data) return;
      closeDelete();
      await load();
      onFlash(data.squareSubscriptionCancelled
        ? 'Subscriber deleted and their Square subscription cancelled'
        : 'Subscriber deleted');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const pendingIsPaid = hasActivePaidSubscription(pendingDelete);
  const deleteReady = canSubmitDelete({
    typed: typedEmail,
    subscriber: pendingDelete,
    cancelPaidAcknowledged
  });

  return (
    <div className="card">
      <p style={{ color: 'var(--color-text-light)', marginTop: 0 }}>
        Subscribers sign up and pay at calendar.yourfirstparcel.com. Create an account
        here only for a complimentary subscriber, such as yourself or someone testing.
      </p>

      {/* Not while the modal is open: an error from a refused delete belongs in
          front of the overlay, not behind it, so the modal renders its own. */}
      {error && !pendingDelete && (
        <div className="card card-error" style={{ marginBottom: '16px' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} className="btn btn-secondary" style={{ marginLeft: '12px' }}>
            Dismiss
          </button>
        </div>
      )}

      {issued && (
        <div style={{ ...panelStyle, marginTop: 0, marginBottom: '16px' }}>
          <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Temporary password</h3>
          <p style={{ color: 'var(--color-text)', marginBottom: '4px' }}>
            For <strong>{issued.email}</strong>
          </p>
          <p style={{
            fontFamily: 'monospace',
            fontSize: '1.2rem',
            color: 'var(--color-text)',
            background: 'var(--color-bg)',
            padding: '10px',
            borderRadius: '6px',
            userSelect: 'all'
          }}>
            {issued.password}
          </p>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
            Copy it now. Only the hash is stored, so this cannot be shown again. If it gets
            lost, issue a new one with Reset password.
          </p>
          <button className="btn btn-secondary" onClick={() => setIssued(null)}>Done</button>
        </div>
      )}

      <div style={{ ...panelStyle, marginTop: 0, marginBottom: '20px' }}>
        <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Create a subscriber</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '2 1 240px' }}>
            <label style={cellLabelStyle} htmlFor="subscriber-email">Email</label>
            <input
              id="subscriber-email"
              type="email"
              style={inputStyle}
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div style={{ flex: '1 1 140px' }}>
            <label style={cellLabelStyle} htmlFor="subscriber-plan">Plan</label>
            <select
              id="subscriber-plan"
              style={inputStyle}
              value={plan}
              onChange={e => setPlan(e.target.value)}
            >
              {PLANS.map(value => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
          <button
            className="btn btn-primary"
            onClick={create}
            disabled={busy || email.trim() === ''}
          >
            {busy ? 'Creating' : 'Create'}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Square</th>
              <th>Digest</th>
              <th>Last login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.length === 0 && (
              <tr>
                <td colSpan={7} style={{ color: 'var(--color-text-light)' }}>
                  No subscribers yet. Create one above to try the calendar.
                </td>
              </tr>
            )}
            {subscribers.map(subscriber => {
              const cancelLabel = cancelBadgeLabel(subscriber);
              return (
              <tr key={subscriber.id}>
                <td style={{ fontWeight: 600 }}>{subscriber.email}</td>
                <td>
                  <span className={`badge ${STATUS_BADGE[subscriber.status] || 'badge-secondary'}`}>
                    {subscriber.status.replace(/_/g, ' ')}
                  </span>
                  {/* The whole reason this column changed. A subscriber who has
                      cancelled reads as plain Active until the day it lands,
                      because that is exactly what they are: still paying, still
                      reading, and leaving. */}
                  {cancelLabel && (
                    <div style={{
                      marginTop: '4px',
                      fontSize: '0.78rem',
                      color: 'var(--color-text-muted)',
                      whiteSpace: 'nowrap'
                    }}>
                      {cancelLabel}
                      <div>Reactivate from their account page.</div>
                    </div>
                  )}
                </td>
                <td>{subscriber.plan}</td>
                <td>
                  {subscriber.square_customer_id ? (
                    <a
                      href={`${SQUARE_CUSTOMER_URL}/${subscriber.square_customer_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ ...idCellStyle, color: 'var(--color-primary)' }}
                      title={`Square customer ${subscriber.square_customer_id}`}
                    >
                      {shortId(subscriber.square_customer_id)}
                    </a>
                  ) : (
                    <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                  )}
                  {subscriber.square_subscription_id && (
                    <div
                      style={{ ...idCellStyle, color: 'var(--color-text-muted)', marginTop: '2px' }}
                      title={`Square subscription ${subscriber.square_subscription_id}`}
                    >
                      {shortId(subscriber.square_subscription_id)}
                    </div>
                  )}
                </td>
                <td>
                  {subscriber.digest_enabled
                    ? <span className="badge badge-success">On</span>
                    : <span className="badge badge-secondary">Off</span>}
                </td>
                <td>
                  {subscriber.last_login_at
                    ? new Date(subscriber.last_login_at).toLocaleDateString()
                    : 'Never'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <select
                    style={{ ...inputStyle, width: 'auto', display: 'inline-block' }}
                    value={subscriber.status}
                    onChange={e => changeStatus(subscriber.id, e.target.value)}
                    aria-label={`Status for ${subscriber.email}`}
                  >
                    {STATUSES.map(value => (
                      <option key={value} value={value}>{value.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-secondary"
                    style={{ marginLeft: '6px' }}
                    onClick={() => resetPassword(subscriber.id)}
                  >
                    Reset password
                  </button>
                  <button
                    className="btn btn-danger"
                    style={{ marginLeft: '6px' }}
                    onClick={() => openDelete(subscriber)}
                    aria-label={`Delete ${subscriber.email}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation. The address has to be typed out, and a paying
          subscriber needs the second tick as well, because the typed address
          only proves the right row was picked. */}
      {pendingDelete && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'var(--color-surface)', borderRadius: '8px', padding: '24px',
            maxWidth: '520px', width: '90%', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h3 style={{ marginTop: 0, color: 'var(--color-heading)' }}>Delete subscriber</h3>

            <p style={{ color: 'var(--color-text)' }}>
              This permanently removes <strong>{pendingDelete.email}</strong> and any
              password reset links they hold. It cannot be undone.
            </p>

            {pendingIsPaid && (
              <div className="card card-error" style={{ marginBottom: '16px' }}>
                <strong>This subscriber is paying.</strong> Plan {pendingDelete.plan}, status{' '}
                {pendingDelete.status.replace(/_/g, ' ')}. Deleting them cancels their Square
                subscription first, so they stop being billed and lose access immediately.
              </div>
            )}

            <label style={cellLabelStyle} htmlFor="confirm-delete-email">
              Type {pendingDelete.email} to confirm
            </label>
            <input
              id="confirm-delete-email"
              type="email"
              autoComplete="off"
              style={inputStyle}
              value={typedEmail}
              onChange={e => setTypedEmail(e.target.value)}
            />

            {pendingIsPaid && (
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: '8px',
                marginTop: '14px', color: 'var(--color-text)'
              }}>
                <input
                  type="checkbox"
                  checked={cancelPaidAcknowledged}
                  onChange={e => setCancelPaidAcknowledged(e.target.checked)}
                  style={{ marginTop: '3px' }}
                />
                <span>Cancel their paid subscription and delete</span>
              </label>
            )}

            {error && (
              <div className="card card-error" style={{ marginTop: '16px', marginBottom: 0 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={!deleteReady || deleting}
              >
                {deleting ? 'Deleting' : 'Delete subscriber'}
              </button>
              <button className="btn btn-secondary" onClick={closeDelete} disabled={deleting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarSubscribers;
